import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, Chip, Divider,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/**
 * "What's New" release-notes modal.
 *
 * Shows once per release on the next login / page reload. The user must scroll
 * to the very bottom before the acknowledge button enables; acknowledging stores
 * the release key in localStorage so it doesn't reappear. Bump RELEASE_KEY (and
 * the content) on the next release to show it again.
 */
const RELEASE_KEY = "altelix_whatsnew_ack_v3.21";

type Section = { icon: string; title: string; items: string[] };

const RELEASE = {
  version: "v3.21",
  name: "Pipeline Reliability Update",
  intro:
    "This is a major update to how Squad Finance categorizes, matches, and posts " +
    "your transactions to QuickBooks — built directly from the posting rules your " +
    "team confirmed. Please read through; you can acknowledge at the bottom.",
  sections: [
    {
      icon: "✅",
      title: "Posting accuracy",
      items: [
        "Credit-card debit/credit corrected — a charge now posts to the credit column and a refund to the debit column in QuickBooks. Bank statements are unchanged (deposits in, payments out).",
        "ACH & Zelle are now booked as expenses (Journal Entries) instead of Checks, so they no longer consume sequential check numbers in your register.",
        "Vendor → GL categorization updated to match your approved chart of accounts (rent, payroll, taxes, card/merchant/bank fees, advertising, and more).",
        "Vendor bills no longer default to Net-30 — they follow each vendor's own terms (no imposed due date).",
      ],
    },
    {
      icon: "🔁",
      title: "Invoice matching",
      items: [
        "Amounts must match exactly by default; an invoice that pays for more than one charge is handled as a special case.",
        "Tighter matching window — an invoice and its charge must be within 14 days.",
        "The expense account comes from your vendor rules, not guessed from the invoice document.",
      ],
    },
    {
      icon: "🛡️",
      title: "Safety",
      items: [
        "No more duplicate postings to QuickBooks — a charge that is already posted can never be sent twice.",
        "Possible duplicates are now sent to the Duplicates page for your review instead of being silently dropped — you decide.",
        "Transactions already in QuickBooks are protected from accidental deletion.",
      ],
    },
    {
      icon: "⏱️",
      title: "Reliability — nothing gets stuck",
      items: [
        "The Review Queue is now trustworthy: any item that needs a human GL decision always appears there and can never silently fall off.",
        "The agents keep working — they recover automatically from interruptions and never give up on a transaction.",
        "Items awaiting your decision are flagged as overdue after 5 business days, so nothing waits unnoticed.",
        "No-invoice accounts list updated (9170, 9196, 7175; wire transfers require an invoice only when the description is FedEx Logistics).",
      ],
    },
  ] as Section[],
  footer:
    "Heads-up: the credit-card debit/credit change is significant. When you post " +
    "March's movement, please spot-check in QuickBooks that a charge lands in the " +
    "credit column and a refund in the debit column.",
};

export function WhatsNewDialog() {
  const [open, setOpen] = useState(false);
  const [canAck, setCanAck] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(RELEASE_KEY) !== "1") setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setCanAck(true);
  };

  const acknowledge = () => {
    try { localStorage.setItem(RELEASE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      // No backdrop dismissal — the user must read + acknowledge.
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(12px)", backgroundColor: "rgba(8,12,22,0.7)" } } }}
      PaperProps={{ sx: { background: "var(--c-bg-elevated)", border: "1px solid rgba(108,92,231,0.2)", borderRadius: 4, overflow: "hidden", maxHeight: "86vh" } }}
    >
      <Box sx={{ height: 3, background: "linear-gradient(90deg, #6c5ce7, #00cec9, #a29bfe)" }} />
      <Box sx={{ px: 4, pt: 3.5, pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
          <AutoAwesomeIcon sx={{ color: "var(--c-link-accent)" }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "var(--c-text-primary)" }}>
            What's New
          </Typography>
          <Chip label={RELEASE.version} size="small" color="primary" sx={{ ml: 0.5, fontWeight: 700 }} />
        </Box>
        <Typography variant="subtitle2" sx={{ color: "var(--c-link-accent)", fontWeight: 700 }}>
          {RELEASE.name}
        </Typography>
      </Box>

      <DialogContent dividers onScroll={onScroll} sx={{ px: 4, py: 2 }}>
        <Typography variant="body2" sx={{ color: "var(--c-text-secondary)", mb: 2 }}>
          {RELEASE.intro}
        </Typography>

        {RELEASE.sections.map((s) => (
          <Box key={s.title} sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--c-text-primary)", mb: 0.75 }}>
              {s.icon} {s.title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {s.items.map((it, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ color: "var(--c-text-secondary)", mb: 0.75, lineHeight: 1.55 }}>
                  {it}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ p: 1.5, borderRadius: 2, background: "var(--c-overlay-2)", border: "1px solid var(--c-overlay-4)" }}>
          <Typography variant="body2" sx={{ color: "var(--c-text-primary)", fontWeight: 500 }}>
            ⚠️ {RELEASE.footer}
          </Typography>
        </Box>

        <Typography ref={bottomRef} variant="caption" sx={{ display: "block", textAlign: "center", color: "var(--c-text-muted)", mt: 2 }}>
          — end of release notes —
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Typography variant="caption" sx={{ color: "var(--c-text-muted)" }}>
          {canAck ? "Thanks for reading." : "Scroll to the end to continue…"}
        </Typography>
        <Button variant="contained" disabled={!canAck} onClick={acknowledge}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
